import { useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { SearchIcon } from "@fawkes/ui";
import { useTranslate } from "@refinedev/core";

/**
 * De vestigingkiezer van het factuuroverzicht, als gedeelde bouwsteen.
 *
 * <p>Een lijst met een zoekveld, "Alles aan" en aanvinkbare vestigingen. Bij negentien vestigingen is
 * "alles behalve deze twee" anders een reeks losse klikken; met het zoekveld zet je een hele groep
 * ("asiel") in één keer aan. Wat je aan hebt staan komt bovenaan, zodat je je eigen selectie kunt
 * uitzetten zonder ernaar te zoeken.
 *
 * <p>De opmaak hangt aan de `fawkes-fin__multi*`-klassen — die stonden al in de stylesheet voor het
 * factuuroverzicht en zijn hier niet gekopieerd maar hergebruikt.
 */
export function VestigingFilter({ label, opties, gekozen, onChange, uitlijning = "links", zoekLabel, leegLabel, weergave }: {
  label: string;
  /** Alle keuzes, in de volgorde waarin ze horen te staan. */
  opties: string[];
  gekozen: string[];
  onChange: (volgende: string[]) => void;
  /** "rechts" laat de lijst naar links groeien — voor een kiezer die rechts in beeld staat. */
  uitlijning?: "links" | "rechts";
  /**
   * Voor een kiezer van iets anders dan vestigingen — een status of een onderdeel: de tekst in het
   * zoekveld, de melding als niets past, en hoe een keuze heet als de waarde een code is. Zonder deze
   * drie blijft het de vestigingkiezer.
   */
  zoekLabel?: string;
  leegLabel?: string;
  weergave?: (waarde: string) => string;
}) {
  const t = useTranslate();
  const [open, setOpen] = useState(false);
  const [zoek, setZoek] = useState("");
  const toon = weergave ?? ((v: string) => v);

  // Klik ernaast (of Escape) klapt het menu dicht — een lijst die alleen sluit door hem weer aan te
  // tikken staat in de weg van het scherm eronder.
  useEffect(() => {
    if (!open) return;
    const sluit = () => setOpen(false);
    const buiten = (e: MouseEvent) => {
      if ((e.target as HTMLElement | null)?.closest(".fawkes-fin__multi")) return;
      sluit();
    };
    const toets = (e: KeyboardEvent) => { if (e.key === "Escape") sluit(); };
    document.addEventListener("mousedown", buiten);
    document.addEventListener("keydown", toets);
    return () => {
      document.removeEventListener("mousedown", buiten);
      document.removeEventListener("keydown", toets);
    };
  }, [open]);

  const zichtbaar = useMemo(() => {
    const naald = zoek.trim().toLowerCase();
    const gefilterd = naald ? opties.filter((v) => toon(v).toLowerCase().includes(naald)) : opties;
    // Aangevinkte vestigingen bovenaan; binnen elke groep blijft de gegeven volgorde staan.
    return [...gefilterd].sort((a, b) => Number(gekozen.includes(b)) - Number(gekozen.includes(a)));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `toon` is per render nieuw; de weergave verandert niet
  }, [opties, zoek, gekozen]);

  const allesAan = zichtbaar.length > 0 && zichtbaar.every((v) => gekozen.includes(v));

  return (
    <div className={uitlijning === "rechts" ? "fawkes-fin__multi fawkes-fin__multi--rechts" : "fawkes-fin__multi"}>
      <span className="fui-field__label">{label}</span>
      <button
        type="button"
        className="fawkes-fin__multi-btn"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span>
          {gekozen.length === 0
            ? t("financieel.filter.alle")
            : gekozen.length === 1
              ? toon(gekozen[0])
              : t("financieel.filter.gekozen", { args: [String(gekozen.length)] })}
        </span>
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {open && (
        <div className="fawkes-fin__multi-menu" role="group" aria-label={label}>
          <label className="fawkes-fin__multi-zoek">
            <SearchIcon width={14} height={14} aria-hidden="true" />
            <input
              value={zoek}
              autoFocus
              placeholder={zoekLabel ?? t("financieel.filter.vestigingZoek")}
              aria-label={zoekLabel ?? t("financieel.filter.vestigingZoek")}
              onChange={(e) => setZoek(e.target.value)}
            />
          </label>
          {/* "Alles aan" werkt op wat je ziet: typ "asiel" en je zet die groep in één klik aan. */}
          <button
            type="button"
            className="fawkes-fin__multi-all"
            onClick={() =>
              onChange(allesAan
                ? gekozen.filter((v) => !zichtbaar.includes(v))
                : [...new Set([...gekozen, ...zichtbaar])])}
          >
            {allesAan ? t("financieel.filter.allesUit") : t("financieel.filter.allesAan")}
          </button>
          <div className="fawkes-fin__multi-list">
            {zichtbaar.length === 0 && (
              <p className="fawkes-fin__multi-leeg">{leegLabel ?? t("financieel.filter.vestigingGeen")}</p>
            )}
            {zichtbaar.map((v) => (
              <label key={v}>
                <input
                  type="checkbox"
                  checked={gekozen.includes(v)}
                  onChange={() =>
                    onChange(gekozen.includes(v) ? gekozen.filter((x) => x !== v) : [...gekozen, v])}
                />
                <span>{toon(v)}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
